import { useState, type ReactNode } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  convertWeight,
  exerciseTraits,
  fetchMaxesMap,
  loadStepFor,
  modelFromSource,
  type Exercise,
  type Locale,
  type ModelChangedRow,
  type ModelSource,
  type ModelSourceDay,
  type ModelSourceRow,
  type ProgramModel,
  type RoundType,
  type UnitsPreference,
} from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import { Button } from "../primitives/Button";
import { Dialog } from "../primitives/Dialog";
import { TextField } from "../primitives/TextField";
import { writeError, type DurableWriteFn } from "../seams";
import type { UiTheme } from "../theme";
import { makeModelSaveStyles } from "./modelSaveStyles";
import { ModelSavedView, type SavedModel } from "./ModelSavedView";

type Failure = "maxes" | "cap" | "save";

const NOOP = () => {};

/** What a model is made from: a program's days, or a logged session's one. */
export type ModelDialogSource = Pick<ModelSource, "focus"> & {
  days: readonly ModelSourceDay[];
  roundTypes: readonly RoundType[];
  sessionsPerWeek: number;
  /** Empty: a fallback that says the shape. */
  description: string;
  locale: Locale | null;
  /** A row's day in the summary. Absent: the row says its exercise only. */
  dayLabel?: (day: number) => string;
  /** Sets a logged session left out as warm-up. */
  warmUps?: number;
  /** Bodyweight exercises the catalog may not flag (workoutToSlotDrafts). */
  bodyweight?: readonly string[];
};

export type ModelSaveDialogProps = {
  theme: UiTheme;
  /** Read at save, so a source that cannot be read fails as the save does. */
  source: () => ModelDialogSource;
  defaultName: string;
  /** Which exercises are bodyweight. null while the host loads it: no save. */
  catalog: Exercise[] | null;
  /** The author's unit: the loads are read in it, the MAXes converted to it. */
  unit: UnitsPreference;
  apiFetch: ProgramApiFetch;
  durableWrite: DurableWriteFn;
  resolveName: (slug: string, snapshot?: string) => string;
  /** The saved model, to plan a program from it. Absent: no such action. */
  onPlanFromModel?: (model: Omit<ProgramModel, "id">) => void;
  /** #368: the diary's «Create program». The title says it, and a save goes
   *  straight to `onPlanFromModel`, with no summary step. */
  createProgram?: boolean;
  /** Under the name: what the model is made of (the picked days). */
  intro?: ReactNode;
  onClose: () => void;
};

// #354: «Save as model». Asks a name, turns the source into a model
// (modelFromSource, against the MAXes of now) and says which rows lost their
// load progression, which have no load and which the model holds differently.
export function ModelSaveDialog({
  theme,
  source,
  defaultName,
  catalog,
  unit,
  apiFetch,
  durableWrite,
  resolveName,
  onPlanFromModel,
  createProgram = false,
  intro,
  onClose,
}: ModelSaveDialogProps) {
  const { t, i18n } = useTranslation();
  const styles = makeModelSaveStyles(theme);
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<Failure | null>(null);
  const [saved, setSaved] = useState<SavedModel | null>(null);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy || !catalog) return;
    setBusy(true);
    setFailed(null);
    try {
      const src = source();
      // No MAXes, no save: rows would go to history by mistake.
      let maxesKg: Map<string, number>;
      try {
        maxesKg = await fetchMaxesMap(apiFetch);
      } catch (e) {
        console.warn("[library] save_model_maxes", e instanceof Error ? e.message : e);
        setFailed("maxes");
        return;
      }
      const maxes = new Map([...maxesKg].map(([slug, kg]) => [slug, convertWeight(kg, "kg", unit)]));
      // The fallback is written in the source's language, not the reader's.
      const written = i18n.getFixedT(src.locale ?? "en");
      const out = modelFromSource({
        name: trimmed,
        description:
          src.description.trim() ||
          written("library.modelDescFallback", {
            days: written("library.modelShapeDays", { count: src.days.length, defaultValue: "{{count}} days" }),
            rounds: written("editorV3.tetrad.roundsA11y", {
              count: src.roundTypes.length,
              defaultValue: "{{count}} rounds",
            }),
            defaultValue: "{{days}}, {{rounds}}.",
          }),
        locale: src.locale,
        focus: src.focus,
        roundTypes: src.roundTypes,
        sessionsPerWeek: src.sessionsPerWeek,
        days: src.days,
        maxes,
        bodyweight: new Set([...catalog.filter((e) => exerciseTraits(e).isBodyweight).map((e) => e.slug), ...(src.bodyweight ?? [])]),
        loadQuantum: loadStepFor(unit),
      });
      // Mobile may still queue it, if the connection drops after the read.
      const result = await durableWrite({
        method: "POST",
        path: "/api/templates/models?scope=mine",
        body: out.model,
      });
      const err = writeError(result);
      if (err) {
        console.warn("[library] save_model_failed", err.message);
        const code = (err as unknown as { body?: { error?: unknown } }).body?.error;
        setFailed(code === "model_cap" ? "cap" : "save");
        return;
      }
      const label = (r: ModelSourceRow) => {
        const exercise = resolveName(r.exerciseId, src.days[r.day]?.slots[r.slot]?.exerciseName);
        if (!src.dayLabel) return exercise;
        return t("library.modelRow", { exercise, day: src.dayLabel(r.day), defaultValue: "{{exercise}}, {{day}}" });
      };
      const changedRow = (r: ModelChangedRow) => ({
        row: label(r),
        why: [
          ...(r.byHand ? [t("library.modelChangedByHand", { defaultValue: "hand-set cells dropped" })] : []),
          ...(r.everyRound ? [t("library.modelChangedEveryRound", { defaultValue: "runs every round" })] : []),
        ],
      });
      if (createProgram && onPlanFromModel) {
        onClose();
        onPlanFromModel(out.model);
        return;
      }
      setSaved({
        name: trimmed,
        queued: result.outcome === "queued",
        toHistory: out.toHistory.map(label),
        noLoad: out.noLoad.map(label),
        changed: out.changed.map(changedRow),
        warmUps: src.warmUps ?? 0,
        model: out.model,
      });
    } catch (e) {
      console.warn("[library] save_model_failed", e instanceof Error ? e.message : e);
      setFailed("save");
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return <ModelSavedView theme={theme} saved={saved} onPlanFromModel={onPlanFromModel} onClose={onClose} />;
  }

  const nameMissing = name.trim() === "";
  // Why Save is off, said in words beside it.
  const blocked = nameMissing
    ? t("library.modelNameEmpty", { defaultValue: "Give it a name to save." })
    : !catalog
      ? t("library.modelWaitCatalog", { defaultValue: "Loading the exercises…" })
      : null;
  const failure =
    failed === "maxes"
      ? t("library.modelMaxesError", { defaultValue: "Reading your MAXes needs a connection. Try again online." })
      : failed === "cap"
        ? t("library.modelCapError", {
            defaultValue: "You have 100 models, the most you can keep. Delete one in New program, My models.",
          })
        : failed === "save"
          ? t("library.modelSaveError", { defaultValue: "Could not save the model. Try again." })
          : null;

  return (
    <Dialog
      theme={theme}
      visible
      // Android back while the POST runs: stay.
      onRequestClose={busy ? NOOP : onClose}
      dismissOnBackdropPress={!busy}
      title={
        createProgram
          ? t("mobile.workouts.createProgram")
          : t("library.saveAsModel", { defaultValue: "Save as model" })
      }
    >
      <TextField
        theme={theme}
        value={name}
        onChangeText={setName}
        autoFocus
        accessibilityLabel={t("library.modelName", { defaultValue: "Model name" })}
        maxLength={80}
        editable={!busy}
      />
      <Text style={styles.muted}>
        {t("library.modelHint", { defaultValue: "Loads become a % of your MAX." })}
      </Text>
      {intro}
      {blocked ? (
        <Text style={styles.muted} accessibilityLiveRegion="polite" aria-live="polite">
          {blocked}
        </Text>
      ) : null}
      {failure ? (
        <Text style={styles.error} accessibilityLiveRegion="polite" aria-live="polite">
          {failure}
        </Text>
      ) : null}
      <View style={styles.actions}>
        <Button
          theme={theme}
          variant="ghost"
          label={t("program.cancel", { defaultValue: "Cancel" })}
          onPress={onClose}
          disabled={busy}
        />
        <Button
          theme={theme}
          label={t("program.save", { defaultValue: "Save" })}
          onPress={() => void save()}
          loading={busy}
          disabled={blocked !== null}
          accessibilityHint={blocked ?? undefined}
        />
      </View>
    </Dialog>
  );
}
